import { useEffect, useRef } from 'react'
import type { ChatMessageView } from '../chat'
import { refreshSession } from '../client'

interface ChatEvent {
  type: 'message' | string
  message?: ChatMessageView
  threadId?: string
}

/**
 * WebSocket к /chat/ws — авторизация через httpOnly cookie `edpad_jwt`,
 * браузер прикладывает cookie автоматически на same-origin connect.
 * Авто-reconnect при разрыве (exp backoff: 1s → 30s).
 * onMessage вызывается на каждое входящее ChatEvent.type='message'.
 */
export function useChatSocket(enabled: boolean, onMessage: (msg: ChatMessageView) => void) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<number | null>(null)
  const reconnectDelayRef = useRef(1000)
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  useEffect(() => {
    if (!enabled) return

    let stopped = false

    const connect = () => {
      if (stopped) return

      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const url = apiBase.startsWith('http')
        ? apiBase.replace(/^http/, 'ws') + '/chat/ws'
        : `${proto}//${window.location.host}${apiBase}/chat/ws`

      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        reconnectDelayRef.current = 1000  // reset backoff
      }
      ws.onmessage = (e) => {
        try {
          const ev: ChatEvent = JSON.parse(e.data)
          if (ev.type === 'message' && ev.message) {
            onMessageRef.current(ev.message)
          }
        } catch {
          // ignore (probably 'pong')
        }
      }
      ws.onclose = (ev) => {
        if (stopped) return
        const scheduleReconnect = () => {
          if (stopped) return
          // Exponential backoff
          reconnectTimer.current = window.setTimeout(connect, reconnectDelayRef.current)
          reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30_000)
        }
        // 1008 (policy violation) — сервер отверг токен: access-cookie живёт час,
        // перед переподключением тихо обновляем сессию (single-flight с REST).
        if (ev.code === 1008) {
          void refreshSession().then((outcome) => { if (outcome !== 'failed') scheduleReconnect() })
        } else {
          scheduleReconnect()
        }
      }
      ws.onerror = () => {
        // onclose будет вызван после
      }
    }

    connect()

    return () => {
      stopped = true
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [enabled])
}
